import { useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import apiClient from "../../api/apiClient";
import API_CONFIG from "../../config/api";
import useBusinessResource from "../../hooks/useBusinessResource";
import { invalidateBusinessResources } from "../../utils/businessResourceFreshness";

const errorMessage = (error) => error?.response?.data?.message || error?.message || "Impossible de vérifier l’établissement du dossier.";

export default function useShipmentWarehouse({ open, shipment, companyId, selectedWarehouse }) {
  const { user } = useAuth();
  const client = useQueryClient();
  const warehouseId = shipment ? shipment.warehouse_id ?? null : selectedWarehouse;
  const shipmentId = shipment?.id ?? null;
  const scope = { userId: user?.id, companyId, warehouseId, moduleScope: "logistics" };
  const selection = JSON.stringify([user?.id, companyId, selectedWarehouse]);
  const openingSelection = useRef(null);
  if (!open) openingSelection.current = null;
  else if (openingSelection.current === null) openingSelection.current = selection;
  const contextError = open && openingSelection.current !== selection ? "Le contexte a changé. Fermez puis rouvrez le formulaire." : null;
  const context = JSON.stringify([open, user?.id, companyId, selectedWarehouse, shipmentId, warehouseId]);
  const current = useRef({ context, version: 0 });
  if (current.current.context !== context) current.current = { context, version: current.current.version + 1 };
  const load = useCallback(async (signal) => {
    const response = await apiClient.get(`${API_CONFIG.API_URL}/logistics/shipments/warehouse-context`, {
      signal, headers: { "X-Company-Id": String(companyId) },
      params: { company_id: companyId, ...(shipmentId ? { shipment_id: shipmentId } : { warehouse_id: warehouseId }) },
    });
    const data = response.data?.data;
    if (!response.data?.success || Number(data?.company_id) !== Number(companyId) ||
      (data.shipment_id ?? null) !== shipmentId) throw new Error("Le contexte du dossier a changé. Rouvrez le formulaire.");
    if ((data.warehouse?.id ?? null) !== (warehouseId ?? null)) {
      throw new Error("L’établissement de ce dossier a changé. Fermez puis rouvrez le formulaire.");
    }
    if (!shipmentId && !data.warehouse) throw new Error("Sélectionnez un établissement avant de créer le dossier.");
    return data;
  }, [companyId, shipmentId, warehouseId]);
  const resource = useBusinessResource({ ...scope, resource: "logistics-shipment-warehouse", parameters: [shipmentId],
    scopeLevel: "company", enabled: open && !contextError, active: open && !contextError, load });
  return {
    ...resource,
    error: contextError || (resource.error ? errorMessage(resource.error) : null),
    captureContext() {
      const version = current.current.version;
      return () => current.current.version === version;
    },
    async verify() {
      const version = current.current.version;
      if (!open || contextError || !companyId || (shipment && Number(shipment.company_id) !== Number(companyId))) {
        throw new Error("Le contexte a changé. Fermez puis rouvrez le formulaire.");
      }
      if (!shipment && !warehouseId) throw new Error("Sélectionnez un établissement avant de créer le dossier.");
      try {
        const data = await resource.refresh({ force: true });
        if (version !== current.current.version) throw new Error("Le contexte a changé. Fermez puis rouvrez le formulaire.");
        return shipment ? { expected_warehouse_id: data.warehouse?.id ?? null } : { warehouse_id: data.warehouse.id };
      } catch (error) { throw new Error(errorMessage(error)); }
    },
    saved: () => invalidateBusinessResources(client, { ...scope, warehouseId: null }, "logistics-shipment"),
  };
}
